/**
 * ATTACH-01 (Seth 2026-08-28): "Yes attach this number. Same thing for
 * customers who send in contact forms — attach email or phone if different."
 *
 * The shared brain for "this phone/email reached us but isn't on the account":
 *   - useEndpointKnown: is this number/email anywhere on the account already?
 *     (ESC mirrors + Pegasus-native + the writable contact store)
 *   - useAttachEndpoint: one tap → a `contact` row on the account. The phone
 *     matcher (pegasus_match_phones, MATCH-02) reads contact.phone, so the
 *     moment a number is attached, the NEXT call from it screen-pops the 360.
 *
 * ESC mirrors stay read-only (DEC-22) — attaching writes the Pegasus contact
 * store, never the mirror.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export const last10 = (s: string | null | undefined): string => {
  const d = (s ?? "").replace(/\D/g, "");
  return d.length >= 10 ? d.slice(-10) : d;
};
const lowmail = (s: string | null | undefined) => (s ?? "").trim().toLowerCase();

export function prettyPhone(s: string | null | undefined): string {
  const d = last10(s);
  if (d.length !== 10) return s ?? "";
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}

/** Every phone (last-10) and email already on the account, across ESC mirrors,
 *  the Pegasus-native record, and the contact store. */
export function useAccountEndpoints(custno: string | null | undefined) {
  return useQuery({
    queryKey: ["account_endpoints", custno],
    enabled: !!custno,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<{ phones: Set<string>; emails: Set<string> }> => {
      const [mc, ml, pc, ct] = await Promise.all([
        supabase.from("mirror_customer").select("phone1, phone2, phone3, phone4").eq("custno", custno!),
        supabase.from("mirror_location").select("phone1, phone2, phone3, phone4, phone5, phone6, email, email2, email3, email4, email5, email6").eq("custno", custno!),
        supabase.from("pegasus_customer").select("phone, email").eq("custno", custno!),
        supabase.from("contact").select("phone, email").eq("custno", custno!),
      ]);
      const phones = new Set<string>();
      const emails = new Set<string>();
      const eat = (row: Record<string, unknown>) => {
        for (const [k, v] of Object.entries(row)) {
          if (v == null || typeof v !== "string" || !v.trim()) continue;
          if (k.startsWith("phone")) {
            const p = last10(v);
            if (p.length === 10) phones.add(p);
          } else if (k.startsWith("email")) {
            emails.add(lowmail(v));
          }
        }
      };
      for (const r of (mc.data ?? []) as Record<string, unknown>[]) eat(r);
      for (const r of (ml.data ?? []) as Record<string, unknown>[]) eat(r);
      for (const r of (pc.data ?? []) as Record<string, unknown>[]) eat(r);
      for (const r of (ct.data ?? []) as Record<string, unknown>[]) eat(r);
      return { phones, emails };
    },
  });
}

/** One tap → a contact row on the account. `name` should be the person's name
 *  when known (AI extract / provider contact), else a plain label. */
export function useAttachEndpoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      custno: string;
      name: string;
      phone?: string | null;
      email?: string | null;
      source: "call" | "inbox";
      note?: string;
    }) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("contact").insert({
        custno: v.custno,
        locno: null,
        name: v.name.trim() || (v.phone ? "Phone contact" : "Email contact"),
        phone: v.phone ? prettyPhone(v.phone) : null,
        email: v.email ? lowmail(v.email) : null,
        source: v.source,
        notes: v.note ?? (v.source === "call" ? "Attached from a phone call" : "Attached from the inbox"),
        created_by: u?.user?.email ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contacts"] });
      qc.invalidateQueries({ queryKey: ["account_endpoints"] });
    },
  });
}
